import dayjs from "dayjs";
import { Link } from "react-router";
import { Mail, MapPin, Phone } from "lucide-react";

import { cn } from "@/lib/utils";
import { formatMoney, formatMoneyCompact, formatNumber } from "@/lib/format";
import type {
  AwardedItem,
  AwardWinner,
  SupplierAward,
  WinnerCompany,
} from "@/types/domain";
import { departmentLabel } from "../lineHelpers";

/**
 * Teléfonos, correos y dirección fiscal de una empresa, de OSCE y SUNAT. Repite
 * la lectura de `fromCompany` (`useContractingDetailModel.ts`); extraerla está
 * pendiente (spec del onboarding de proveedor, §9).
 */
function companyContact(company: WinnerCompany): {
  phones: string[];
  emails: string[];
  address: string | null;
} {
  const osce = company.extracteddata?.osce;
  const sunat = company.extracteddata?.consultarucsunat;
  const basicInfo = osce?.basicInfo ?? null;
  const phones: string[] =
    basicInfo?.proveedorT01?.telefonos ??
    (basicInfo?.datosGenerales?.telefono ? [basicInfo.datosGenerales.telefono] : []);
  const emails: string[] =
    basicInfo?.proveedorT01?.emails ??
    (basicInfo?.datosGenerales?.correoElectronico
      ? [basicInfo.datosGenerales.correoElectronico]
      : []);
  const address: string | null =
    sunat?.["Domicilio Fiscal"] ??
    osce?.resumen?.datosSunat?.domicilioFiscal ??
    basicInfo?.proveedorT01?.domicilioFiscal ??
    null;
  return {
    phones: phones.filter(Boolean),
    emails: emails.filter(Boolean),
    // SUNAT devuelve "-" cuando no tiene el dato.
    address: address && address.trim() !== "-" ? address.replace(/\s+/g, " ").trim() : null,
  };
}

/**
 * "Contrato firmado hoy", "ayer" o "hace N días", con el color de la urgencia.
 * Sin fecha de firma cae a la de la buena pro.
 */
function workAge(award: SupplierAward): {
  label: string;
  dotClass: string;
  textClass: string;
} {
  const date = award.contractAt ?? award.awardAt;
  const event = award.contractAt ? "Contrato firmado" : "Buena pro";
  if (!date) {
    return { label: "Contratada", dotClass: "bg-slate-400", textClass: "text-slate-600" };
  }
  const days = dayjs().startOf("day").diff(dayjs(date).startOf("day"), "day");
  const when = days <= 0 ? "hoy" : days === 1 ? "ayer" : `hace ${days} días`;
  // Recién firmado el contrato es cuando el ganador sale a comprar materiales.
  if (days <= 7) {
    return {
      label: `${event} ${when} · está comprando ahora`,
      dotClass: "bg-red-500",
      textClass: "text-red-600",
    };
  }
  if (days <= 15) {
    return { label: `${event} ${when}`, dotClass: "bg-orange-500", textClass: "text-orange-700" };
  }
  return { label: `${event} ${when}`, dotClass: "bg-slate-500", textClass: "text-slate-600" };
}

function ContactChips({ company }: { company: WinnerCompany }) {
  const { phones, emails, address } = companyContact(company);
  return (
    <div className="space-y-2">
      {address && (
        <p className="flex items-start gap-1.5 text-[13px] leading-[1.45] text-slate-600">
          <MapPin className="mt-0.5 size-3.5 shrink-0 text-emerald-600" />
          {address}
        </p>
      )}
      {phones.length === 0 && emails.length === 0 ? (
        <p className="text-[13px] text-slate-500">
          Sin teléfono ni correo en OSCE · RUC {company.ruc} ·{" "}
          <Link
            to={`/companies/${company.companyId}`}
            className="cursor-pointer font-semibold text-emerald-700 underline underline-offset-2"
          >
            Ver ficha
          </Link>
        </p>
      ) : (
        <ContactLinks phones={phones} emails={emails} />
      )}
    </div>
  );
}

function ContactLinks({ phones, emails }: { phones: string[]; emails: string[] }) {
  return (
    <div className="flex flex-wrap gap-2">
      {phones.map((phone) => (
        <a
          key={phone}
          href={`tel:${phone.replace(/\s+/g, "")}`}
          className="inline-flex cursor-pointer items-center gap-1.5 rounded-full bg-slate-100 px-3.5 py-2 text-[13.5px] font-semibold text-slate-800 hover:bg-slate-200"
        >
          <Phone className="size-3.5 text-emerald-600" />
          {phone}
        </a>
      ))}
      {emails.map((email) => (
        <a
          key={email}
          href={`mailto:${email}`}
          className="inline-flex cursor-pointer items-center gap-1.5 break-all rounded-full bg-slate-100 px-3.5 py-2 text-[13.5px] font-semibold text-slate-800 hover:bg-slate-200"
        >
          <Mail className="size-3.5 text-emerald-600" />
          {email}
        </a>
      ))}
    </div>
  );
}

function WinnerBlock({ winner }: { winner: AwardWinner }) {
  const meta = [
    winner.kind === "consortium"
      ? `Consorcio · ${winner.companies.length} integrantes`
      : winner.ruc
        ? `RUC ${winner.ruc}`
        : null,
    winner.awardedAmountPen
      ? `${formatMoneyCompact(winner.awardedAmountPen)} adjudicados`
      : null,
  ].filter(Boolean);

  return (
    <div>
      <p className="text-[19px] font-extrabold tracking-[-0.02em] text-slate-900">
        {winner.name}
      </p>
      {meta.length > 0 && (
        <p className="mt-0.5 text-[13.5px] text-slate-500">{meta.join(" · ")}</p>
      )}
      <div className="mt-3 space-y-3">
        {winner.kind === "consortium"
          ? winner.companies.map((company) => (
              <div key={company.companyId}>
                <p className="mb-1.5 text-[13px] font-semibold text-slate-700">
                  {company.name}
                </p>
                <ContactChips company={company} />
              </div>
            ))
          : winner.companies.map((company) => (
              <ContactChips key={company.companyId} company={company} />
            ))}
      </div>
    </div>
  );
}

/** Una partida que pide lo que vende: cuánto suma, cuánto pide y a qué precio. */
function ItemRow({ item }: { item: AwardedItem }) {
  const unit = item.unit?.toLowerCase() ?? "";
  return (
    <li className="rounded-[10px] bg-emerald-50 px-3.5 py-2.5 text-emerald-900">
      <div className="flex items-start justify-between gap-4">
        <span className="text-[13.5px] font-semibold">{item.description}</span>
        {item.totalPen != null && item.totalPen > 0 && (
          <span className="shrink-0 text-[14px] font-extrabold tabular-nums">
            {formatMoney(item.totalPen)}
          </span>
        )}
      </div>
      {item.quantity != null && (
        <p className="mt-0.5 text-[12.5px] font-medium tabular-nums text-emerald-700">
          {formatNumber(item.quantity)} {unit}
          {item.unitPricePen != null &&
            item.unitPricePen > 0 &&
            ` × ${formatMoney(item.unitPricePen, "S/", true)}`}
        </p>
      )}
    </li>
  );
}

/**
 * Una buena pro reciente: cuándo, dónde y todas las partidas que pide de lo que
 * vende, con el ganador y su contacto (el onboarding se la regala desbloqueada).
 */
export function AwardCard({
  award,
  highlight,
}: {
  award: SupplierAward;
  highlight: boolean;
}) {
  const age = workAge(award);
  const firstPhone = award.winners
    .flatMap((winner) => winner.companies)
    .flatMap((company) => companyContact(company).phones)[0];

  return (
    <article
      className={cn(
        "rounded-[20px] border bg-white p-4 sm:p-5",
        highlight
          ? "border-emerald-300 shadow-[0_12px_32px_-16px_rgba(5,150,105,0.45)]"
          : "border-slate-200",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <span className={cn("flex items-center gap-2 text-[13px] font-semibold", age.textClass)}>
          <span className={cn("size-2 shrink-0 rounded-full", age.dotClass)} />
          {age.label}
        </span>
        {award.department && (
          <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-[12px] font-semibold text-slate-600">
            {departmentLabel(award.department)}
          </span>
        )}
      </div>

      <ul className="mt-3 space-y-1.5">
        {award.items.map((item, index) => (
          <ItemRow key={`${item.description}-${index}`} item={item} />
        ))}
      </ul>

        <div className="mt-4">
          <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.12em] text-emerald-600">
            Ganó la buena pro · tu próximo cliente
          </p>
          <div className="mt-2 space-y-4">
            {award.winners.length > 0 ? (
              award.winners.map((winner) => (
                <WinnerBlock key={`${winner.kind}-${winner.name}`} winner={winner} />
              ))
            ) : (
              <p className="text-[13.5px] text-slate-500">
                El OECE todavía no publica al ganador de esta obra.
              </p>
            )}
          </div>
          {firstPhone && (
            <a
              href={`tel:${firstPhone.replace(/\s+/g, "")}`}
              className="mt-4 flex w-full cursor-pointer items-center justify-center gap-2 rounded-full bg-emerald-600 px-6 py-3.5 text-[15px] font-bold text-white transition-colors hover:bg-emerald-700"
            >
              <Phone className="size-4" />
              Llamar ahora
            </a>
          )}
        </div>
    </article>
  );
}
